import React, { useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import { useStoreValue, storeSet } from '../../../../store';
import ButtonFlat from '../../../../components/ButtonWrapper/components/ButtonFlat';
import ButtonFlatAccent from '../../../../components/ButtonWrapper/components/ButtonFlatAccent';
import CrownIcon from '../../../../icons/CrownIcon';

const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) return '—';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatPrice = (priceCents) =>
    priceCents === 0 ? 'Gratis' : `€${priceCents / 100}`;

const PAYMENTS = [
    { id: 'INV-2026-02', date: '15 Feb 2026', amount: '€19,00', status: 'Pagata', downloadHref: '#' },
    { id: 'INV-2026-01', date: '15 Gen 2026', amount: '€19,00', status: 'Pagata', downloadHref: '#' },
    { id: 'INV-2025-12', date: '15 Dic 2025', amount: '€19,00', status: 'Pagata', downloadHref: '#' },
];

const ProfileSubscription = () => {
    const userCounters = useStoreValue('userCounters');
    const userData = useStoreValue('userData');
    const plans = useStoreValue('plans');
    const plansLoading = useStoreValue('plansLoading');

    useEffect(() => {
        storeSet('fetchUserCounters');
        storeSet('fetchPlans');
    }, []);

    return (
        <section className="workbenchProfile workbenchProfile--subscription">

            {/* Section 1: Current Plan */}
            <div className="subCurrentPlan">
                <div className="subCurrentPlan__headerWrapper">
                    <div className="subCurrentPlan__header">
                        <h3 className="workbenchProfile__sectionTitle">
                            {__('Piano FREE', 'soundbetween')}
                        </h3>
                        <p className="subCurrentPlan__since">
                            {__('Attivo dal 15 Gennaio 2023', 'soundbetween')}
                        </p>
                    </div>
                    <CrownIcon />
                </div>
                <dl className="subCurrentPlan__stats">
                    <div className="subCurrentPlan__stat">
                        <dt>{__('Progetti Attivi', 'soundbetween')}</dt>
                        <dd>{userCounters?.activeProjects ?? '—'} / {userCounters?.maxProjects ?? '-'}</dd>
                    </div>
                    <div className="subCurrentPlan__stat">
                        <dt>{__('Storage Usato', 'soundbetween')}</dt>
                        <dd>{formatBytes(userCounters?.usedStorageBytes)} / {userCounters?.maxStorage ?? '-'}</dd>
                    </div>
                    <div className="subCurrentPlan__stat">
                        <dt>{__('Collaboratori', 'soundbetween')}</dt>
                        <dd>{userCounters?.invitedCollaborators ?? '—'} / {userCounters?.maxCollaborators ?? '—'}</dd>
                    </div>
                    <div className="subCurrentPlan__stat">
                        <dt>{__('Prossimo Rinnovo', 'soundbetween')}</dt>
                        <dd>—</dd>
                    </div>
                </dl>
            </div>

            {/* Section 2: Available Plans */}
            <div className="subPlans">
                <h3 className="workbenchProfile__sectionTitle">
                    {__('Piani Disponibili', 'soundbetween')}
                </h3>
                {plansLoading ? (
                    <p className="subPlans__loading">{__('Caricamento piani…', 'soundbetween')}</p>
                ) : (
                    <ul className="subPlans__grid" role="list">
                        {plans.map((plan, index) => {
                            // TODO: compare plan.id with userData.planId once the field is available on the user object
                            const isCurrent = userData?.planId
                                ? plan.id === userData.planId
                                : index === 0;
                            const price = formatPrice(plan.priceCents);
                            const showPeriod = plan.priceCents !== 0;
                            return (
                                <li
                                    key={plan.id}
                                    className={`planCard${isCurrent ? ' planCard--current' : ''}`}
                                >
                                    <div className="planCard__header">
                                        <CrownIcon />
                                        <span className="planCard__name">{plan.name}</span>
                                    </div>
                                    <div className="planCard__price">
                                        <span className="planCard__amount">{price}</span>
                                        {showPeriod && (
                                            <span className="planCard__period">/mese</span>
                                        )}
                                    </div>
                                    <ul className="planCard__features" role="list">
                                        {plan.features.map((feature) => (
                                            <li key={feature} className="planCard__feature">{feature}</li>
                                        ))}
                                    </ul>
                                    {isCurrent
                                        ? <ButtonFlat size="md" type="button" disabled onClick={() => {}}>
                                            {__('Piano Attuale', 'soundbetween')}
                                        </ButtonFlat>
                                        : <ButtonFlatAccent size="md" type="button" onClick={() => {}}>
                                            {__('Seleziona', 'soundbetween')}
                                        </ButtonFlatAccent>}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Section 3: Payment History */}
            <div className="subPayments">
                <h3 className="workbenchProfile__sectionTitle">
                    {__('Storico Pagamenti', 'soundbetween')}
                </h3>
                <table className="paymentTable">
                    <thead>
                        <tr>
                            <th scope="col">{__('Fattura', 'soundbetween')}</th>
                            <th scope="col">{__('Data', 'soundbetween')}</th>
                            <th scope="col">{__('Importo', 'soundbetween')}</th>
                            <th scope="col">{__('Stato', 'soundbetween')}</th>
                            <th scope="col">
                                <span className="sr-only">{__('Azioni', 'soundbetween')}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {PAYMENTS.map((payment) => (
                            <tr key={payment.id} className="paymentTable__row">
                                <td className="paymentTable__cell paymentTable__cell--id">
                                    {payment.id}
                                </td>
                                <td className="paymentTable__cell paymentTable__cell--date">
                                    {payment.date}
                                </td>
                                <td className="paymentTable__cell paymentTable__cell--amount">
                                    {payment.amount}
                                </td>
                                <td className="paymentTable__cell paymentTable__cell--status">
                                    <span className="paymentBadge paymentBadge--paid">
                                        {payment.status}
                                    </span>
                                </td>
                                <td className="paymentTable__cell paymentTable__cell--action">
                                    <a
                                        href={payment.downloadHref}
                                        className="paymentTable__download"
                                        download
                                    >
                                        {__('Scarica Fattura', 'soundbetween')}
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </section>
    );
};

export default ProfileSubscription;
